import { useCallback } from 'react';
import { findPathAtPoint, findVertexHandleAt, findSegmentIntersection } from './path-renderer';
import { BUFFER_SIZE } from './types';
import type { MapSection, LandTool, SymbolMode, MapEntityArea, MapEntityPath, PlacedSymbol } from './types';
import type { MapPathType, MapRoadQuality, MapLabel } from '@common/schemas/entities/location.schema';

interface CanvasInteractionInput {
    // Viewport
    dimensions: { width: number; height: number };
    isCustomUpload: boolean;
    customMapImageRef?: React.RefObject<HTMLImageElement | null>;
    zoomRef: React.MutableRefObject<number>;
    panRef: React.MutableRefObject<{ x: number; y: number }>;
    // Mode
    activeSection: MapSection;
    activeLandTool: LandTool;
    symbolMode: SymbolMode;
    brushSize: number;
    symbolSize: number;
    activeSymbol: string;
    // World/area
    isPlacingPoint: boolean;
    isDrawingArea: boolean;
    mapEntityAreas: MapEntityArea[];
    selectedWorldUid: string | null;
    inProgressVertices: { x: number; y: number }[];
    inProgressPathVertices: { x: number; y: number }[];
    // Paths
    isDrawingPath: boolean;
    mapEntityPaths: MapEntityPath[];
    selectedPathUid: string | null;
    activePathTypeRef?: React.RefObject<{ pathType: MapPathType; roadQuality?: MapRoadQuality }>;
    // Symbols
    placedSymbols: PlacedSymbol[];
    activeSelectedUid: string | null;
    // Labels
    mapLabels: MapLabel[];
    selectedLabelUid: string | null;
    // Refs
    lastPanPos: React.MutableRefObject<{ x: number; y: number } | null>;
    lastPos: React.MutableRefObject<{ x: number; y: number } | null>;
    selectedSymbolUid: React.MutableRefObject<string | null>;
    dragOffset: React.MutableRefObject<{ dx: number; dy: number }>;
    lastPlacementPos: React.MutableRefObject<{ x: number; y: number } | null>;
    draggingVertex: React.MutableRefObject<{ pathUid: string; index: number } | null>;
    brushQueueRef: React.MutableRefObject<{ x: number; y: number; lx: number; ly: number }[]>;
    // Callbacks
    isPainting: boolean;
    isPanning: boolean;
    setIsPainting: (v: boolean) => void;
    setIsPanning: (v: boolean) => void;
    setCursorPos: (pos: { x: number; y: number } | null) => void;
    setCursorVertex: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
    setPathCursorVertex: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
    setHoveredWorldUid: (uid: string | null) => void;
    setHoveredPathUid: (uid: string | null) => void;
    setActiveSelectedUid: (uid: string | null) => void;
    setSelectedWorldUid: (uid: string | null) => void;
    setSelectedPathUid: (uid: string | null) => void;
    setSelectedLabelUid: (uid: string | null) => void;
    setHoveredLabelUid: (uid: string | null) => void;
    setPlacedSymbols: React.Dispatch<React.SetStateAction<PlacedSymbol[]>>;
    addLabel: (x: number, y: number, text?: string, template?: Partial<MapLabel>) => void;
    updateLabel: (uid: string, updates: Partial<Omit<MapLabel, 'uid'>>) => void;
    onPointPlaced: (x: number, y: number) => void;
    addVertex: (x: number, y: number) => void;
    addPathVertex: (x: number, y: number) => void;
    insertPathVertex: (uid: string, index: number, x: number, y: number) => void;
    movePathVertex: (uid: string, index: number, x: number, y: number) => void;
    finishDrawing: () => void;
    finishPathDrawing: (finalSnap?: { x: number; y: number }) => void;
    onCommit: () => void;
    eraseSymbolsAt: (bx: number, by: number, radius: number) => void;
    spraySymbolsAt: (bx: number, by: number) => void;
    sprayStampsAt: (x: number, y: number, lx: number, ly: number, brush: string) => void;
    snapRiverNode: (x: number, y: number, excludeUid?: string) => { x: number; y: number };
    updatePan: (pan: { x: number; y: number }) => void;
    updateZoom: (zoom: number) => void;
    dirtyBgScreen: React.MutableRefObject<boolean>;
    dirtyMidScreen: React.MutableRefObject<boolean>;
    dirtyFg: React.MutableRefObject<boolean>;
    triggerCameraUpdate: () => void;
    fgCanvasRef: React.RefObject<HTMLCanvasElement | null>;
    brush: string;
    activeSymbols: string[];
    toast: (opts: { title: string; description: string; variant?: string }) => void;
    tLimitReached: string;
    tLimitDesc: string;
}

/** Computes renderWidth/renderHeight/mapWidth/mapHeight from dimensions + custom map. */
function computeRenderDims(
    dimensions: { width: number; height: number },
    isCustomUpload: boolean,
    customMapImageRef?: React.RefObject<HTMLImageElement | null>,
) {
    const { width, height } = dimensions;
    const isCustom = isCustomUpload && !!customMapImageRef?.current;
    const mapWidth = isCustom ? (customMapImageRef?.current?.naturalWidth ?? BUFFER_SIZE) : BUFFER_SIZE;
    const mapHeight = isCustom ? (customMapImageRef?.current?.naturalHeight ?? BUFFER_SIZE) : BUFFER_SIZE;
    let renderWidth = Math.min(width, height);
    let renderHeight = Math.min(width, height);
    if (isCustom) {
        const ratio = mapWidth / mapHeight;
        if (width / height > ratio) { renderHeight = height; renderWidth = height * ratio; }
        else { renderWidth = width; renderHeight = width / ratio; }
    }
    return { mapWidth, mapHeight, renderWidth, renderHeight };
}

/**
 * Returns stable onEvent (mouse down/move/up) and onWheel handlers,
 * extracted from map-canvas.tsx where they lived as inline arrow functions.
 */
export function useCanvasInteraction(input: CanvasInteractionInput) {
    const {
        dimensions, isCustomUpload, customMapImageRef, zoomRef, panRef,
        activeSection, activeLandTool, symbolMode, brushSize, symbolSize, activeSymbol,
        isPlacingPoint, isDrawingArea, mapEntityAreas, selectedWorldUid, inProgressVertices,
        inProgressPathVertices, isDrawingPath, mapEntityPaths, selectedPathUid, activePathTypeRef,
        placedSymbols, activeSelectedUid,
        mapLabels, selectedLabelUid,
        lastPanPos, lastPos, selectedSymbolUid, dragOffset, lastPlacementPos, draggingVertex, brushQueueRef,
        isPainting, isPanning, setIsPainting, setIsPanning,
        setCursorPos, setCursorVertex, setPathCursorVertex,
        setHoveredWorldUid, setHoveredPathUid, setActiveSelectedUid, setSelectedWorldUid, setSelectedPathUid, setPlacedSymbols,
        setSelectedLabelUid, setHoveredLabelUid, addLabel, updateLabel,
        onPointPlaced, addVertex, addPathVertex, insertPathVertex, movePathVertex,
        finishDrawing, finishPathDrawing, onCommit,
        eraseSymbolsAt, spraySymbolsAt, sprayStampsAt, snapRiverNode,
        updatePan, updateZoom,
        dirtyBgScreen, dirtyMidScreen, dirtyFg, triggerCameraUpdate,
        fgCanvasRef, brush, activeSymbols,
        toast, tLimitReached, tLimitDesc,
    } = input;

    const onEvent = useCallback((e: React.MouseEvent, type: 'down' | 'move' | 'up') => {
        const sx = e.nativeEvent.offsetX;
        const sy = e.nativeEvent.offsetY;
        dirtyFg.current = true;

        const { mapWidth, mapHeight, renderWidth, renderHeight } = computeRenderDims(dimensions, isCustomUpload, customMapImageRef);
        const zoom = zoomRef.current;
        const pan = panRef.current;
        const bx = ((sx - pan.x) / (renderWidth * zoom)) * mapWidth;
        const by = ((sy - pan.y) / (renderHeight * zoom)) * mapHeight;

        const toScreen = (x: number, y: number) => ({
            sx: (x / mapWidth) * renderWidth * zoom + pan.x,
            sy: (y / mapHeight) * renderHeight * zoom + pan.y,
        });

        if (type === 'down') {
            if (e.button === 1 || (e.button === 0 && e.altKey)) {
                setIsPanning(true);
                lastPanPos.current = { x: e.clientX, y: e.clientY };
            } else if (e.button === 0) {
                // --- WORLD ---
                if (activeSection === 'world') {
                    if (isPlacingPoint) { onPointPlaced(bx, by); return; }
                    if (isDrawingArea) { addVertex(bx, by); return; }
                    const clickedArea = mapEntityAreas.slice().reverse().find(area => {
                        if (area.vertices.length < 3) return false;
                        let inside = false;
                        const { vertices } = area;
                        for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
                            const xi = vertices[i].x, yi = vertices[i].y;
                            const xj = vertices[j].x, yj = vertices[j].y;
                            if (((yi > by) !== (yj > by)) && (bx < (xj - xi) * (by - yi) / (yj - yi) + xi)) inside = !inside;
                        }
                        return inside;
                    });
                    setSelectedWorldUid(clickedArea ? (clickedArea.uid === selectedWorldUid ? null : clickedArea.uid) : null);
                    return;
                }
                // --- PATHS ---
                if (activeSection === 'paths') {
                    if (isDrawingPath) {
                        let finalBx = bx; let finalBy = by;
                        if (activePathTypeRef?.current?.pathType === 'river' && inProgressPathVertices.length === 0) {
                            const snapped = snapRiverNode(bx, by);
                            finalBx = snapped.x; finalBy = snapped.y;
                        }
                        addPathVertex(finalBx, finalBy);
                        return;
                    }
                    const selectedPath = mapEntityPaths.find(p => p.uid === selectedPathUid);
                    if (selectedPath) {
                        const vIndex = findVertexHandleAt(selectedPath, bx, by, toScreen, 8);
                        if (vIndex >= 0) { draggingVertex.current = { pathUid: selectedPath.uid, index: vIndex }; return; }
                        const insertIndex = findSegmentIntersection(selectedPath, bx, by, toScreen, 10);
                        if (insertIndex >= 0) {
                            insertPathVertex(selectedPath.uid, insertIndex, bx, by);
                            draggingVertex.current = { pathUid: selectedPath.uid, index: insertIndex };
                            return;
                        }
                    }
                    const hitUid = findPathAtPoint(mapEntityPaths, bx, by, toScreen, 12);
                    setSelectedPathUid(hitUid !== selectedPathUid ? hitUid : null);
                    return;
                }
                // --- LABELS ---
                if (activeSection === 'labels') {
                    // Check if a label was clicked
                    // We need a rough bounding box check
                    const clickedLabel = [...mapLabels].reverse().find(label => {
                        const scaledFontSize = label.fontSize;
                        // approximate width based on text length and font size
                        const approxWidth = label.text.length * (scaledFontSize * 0.6);
                        const approxHeight = scaledFontSize;
                        
                        // We do the check in base coordinates (bx, by) vs label (x, y)
                        // If rotated, it's more complex, but we'll assume unrotated for simple selection box
                        // or do a simple distance check for now
                        const dx = bx - label.x;
                        const dy = by - label.y;
                        
                        if (label.rotation) {
                            const cos = Math.cos(-label.rotation);
                            const sin = Math.sin(-label.rotation);
                            const rx = dx * cos - dy * sin;
                            const ry = dx * sin + dy * cos;
                            return Math.abs(rx) <= approxWidth / 2 && Math.abs(ry) <= approxHeight / 2;
                        }
                        return Math.abs(dx) <= approxWidth / 2 && Math.abs(dy) <= approxHeight / 2;
                    });
                    if (clickedLabel) {
                        setSelectedLabelUid(clickedLabel.uid);
                        dragOffset.current = { dx: clickedLabel.x - bx, dy: clickedLabel.y - by };
                        setIsPainting(true);
                        lastPos.current = { x: bx, y: by };
                    } else {
                        const currentSelected = selectedLabelUid ? mapLabels.find(l => l.uid === selectedLabelUid) : undefined;
                        setSelectedLabelUid(null);
                        addLabel(bx, by, undefined, currentSelected);
                        onCommit();
                    }
                    return;
                }
                // --- PAINTING / SYMBOLS ---
                setIsPainting(true);
                lastPos.current = { x: bx, y: by };
                if (activeSection === 'symbols') {
                    if (symbolMode === 'select') {
                        const clicked = [...placedSymbols].reverse().find(sym => Math.hypot(sym.x - bx, sym.y - by) < sym.size / 2);
                        if (clicked) {
                            selectedSymbolUid.current = clicked.uid;
                            dragOffset.current = { dx: clicked.x - bx, dy: clicked.y - by };
                            setActiveSelectedUid(clicked.uid);
                        } else {
                            setActiveSelectedUid(null);
                        }
                    } else if (symbolMode === 'eraser') {
                        eraseSymbolsAt(bx, by, brushSize * 10);
                    } else if (symbolMode === 'single') {
                        if (placedSymbols.length < 2000) {
                            setPlacedSymbols(prev => [...prev, {
                                uid: Math.random().toString(36).substring(2, 9),
                                id: activeSymbol, x: bx, y: by,
                                size: symbolSize, rotation: Math.floor(Math.random() * 30) - 15,
                            }]);
                        } else {
                            toast({ title: tLimitReached, description: tLimitDesc, variant: 'destructive' });
                        }
                    } else if (symbolMode === 'brush') {
                        lastPlacementPos.current = null;
                        spraySymbolsAt(bx, by);
                    }
                } else if (activeSection === 'land' && activeLandTool === 'stamp') {
                    lastPlacementPos.current = null;
                    sprayStampsAt(bx, by, bx, by, brush);
                } else {
                    brushQueueRef.current.push({ x: bx, y: by, lx: bx, ly: by });
                }
            }
        } else if (type === 'move') {
            const rect = e.currentTarget.getBoundingClientRect();
            setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });

            if (isDrawingArea && activeSection === 'world') setCursorVertex({ x: bx, y: by });
            if (isDrawingPath && activeSection === 'paths') {
                let cursorX = bx; let cursorY = by;
                if (activePathTypeRef?.current?.pathType === 'river' && inProgressPathVertices.length === 0) {
                    const snapped = snapRiverNode(bx, by);
                    cursorX = snapped.x; cursorY = snapped.y;
                }
                setPathCursorVertex({ x: cursorX, y: cursorY });
            }
            if (activeSection === 'world' && !isDrawingArea && !isPlacingPoint) {
                const hoveredArea = mapEntityAreas.slice().reverse().find(area => {
                    if (area.vertices.length < 3) return false;
                    let inside = false;
                    const { vertices } = area;
                    for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
                        const xi = vertices[i].x, yi = vertices[i].y;
                        const xj = vertices[j].x, yj = vertices[j].y;
                        if (((yi > by) !== (yj > by)) && (bx < (xj - xi) * (by - yi) / (yj - yi) + xi)) inside = !inside;
                    }
                    return inside;
                });
                setHoveredWorldUid(hoveredArea?.uid ?? null);
            }
            if (activeSection === 'paths' && !isDrawingPath) {
                setHoveredPathUid(findPathAtPoint(mapEntityPaths, bx, by, toScreen, 12));
            }
            if (activeSection === 'labels') {
                const hoveredLabel = [...mapLabels].reverse().find(label => {
                    const approxWidth = label.text.length * (label.fontSize * 0.6);
                    const approxHeight = label.fontSize;
                    const dx = bx - label.x;
                    const dy = by - label.y;
                    if (label.rotation) {
                        const cos = Math.cos(-label.rotation);
                        const sin = Math.sin(-label.rotation);
                        const rx = dx * cos - dy * sin;
                        const ry = dx * sin + dy * cos;
                        return Math.abs(rx) <= approxWidth / 2 && Math.abs(ry) <= approxHeight / 2;
                    }
                    return Math.abs(dx) <= approxWidth / 2 && Math.abs(dy) <= approxHeight / 2;
                });
                setHoveredLabelUid(hoveredLabel ? hoveredLabel.uid : null);
            }

            if (isPanning && lastPanPos.current) {
                const dx = e.clientX - lastPanPos.current.x;
                const dy = e.clientY - lastPanPos.current.y;
                updatePan({ x: panRef.current.x + dx, y: panRef.current.y + dy });
                dirtyBgScreen.current = true; dirtyMidScreen.current = true; dirtyFg.current = true;
                lastPanPos.current = { x: e.clientX, y: e.clientY };
                triggerCameraUpdate();
            } else if (draggingVertex.current) {
                const { pathUid, index } = draggingVertex.current;
                let finalBx = bx; let finalBy = by;
                const draggingPath = mapEntityPaths.find(p => p.uid === pathUid);
                if (draggingPath?.pathType === 'river') {
                    const isExtreme = index === 0 || index === draggingPath.vertices.length - 1;
                    if (isExtreme) { const s = snapRiverNode(bx, by, pathUid); finalBx = s.x; finalBy = s.y; }
                }
                movePathVertex(pathUid, index, finalBx, finalBy);
                triggerCameraUpdate();
            } else if (isPainting) {
                const startX = lastPos.current?.x ?? bx;
                const startY = lastPos.current?.y ?? by;
                if (activeSection === 'symbols') {
                    if (symbolMode === 'select' && selectedSymbolUid.current) {
                        const uid = selectedSymbolUid.current;
                        const offset = dragOffset.current;
                        setPlacedSymbols(prev => prev.map(s => s.uid === uid ? { ...s, x: bx + offset.dx, y: by + offset.dy } : s));
                    } else if (symbolMode === 'eraser') {
                        eraseSymbolsAt(bx, by, brushSize * 10);
                    } else if (symbolMode === 'brush') {
                        spraySymbolsAt(bx, by);
                    }
                } else if (activeSection === 'labels' && selectedLabelUid) {
                    const offset = dragOffset.current;
                    updateLabel(selectedLabelUid, { x: bx + offset.dx, y: by + offset.dy });
                } else if (activeSection === 'land' && activeLandTool === 'stamp') {
                    sprayStampsAt(bx, by, startX, startY, brush);
                } else {
                    brushQueueRef.current.push({ x: bx, y: by, lx: startX, ly: startY });
                }
                lastPos.current = { x: bx, y: by };
            }
        } else if (type === 'up') {
            setIsPainting(false);
            setIsPanning(false);
            selectedSymbolUid.current = null;
            lastPlacementPos.current = null;
            lastPos.current = null;
            lastPanPos.current = null;
            if (draggingVertex.current) { draggingVertex.current = null; onCommit(); }
            if (isPainting) onCommit();
        }
    }, [
        dimensions, isCustomUpload, customMapImageRef, zoomRef, panRef,
        activeSection, activeLandTool, symbolMode, brushSize, symbolSize, activeSymbol,
        isPlacingPoint, isDrawingArea, mapEntityAreas, selectedWorldUid, inProgressVertices,
        mapEntityPaths, selectedPathUid, activePathTypeRef,
        placedSymbols, activeSelectedUid,
        mapLabels, selectedLabelUid,
        lastPanPos, lastPos, selectedSymbolUid, dragOffset, lastPlacementPos, draggingVertex, brushQueueRef,
        isPainting, isPanning, setIsPainting, setIsPanning,
        setCursorPos, setCursorVertex, setPathCursorVertex,
        setHoveredWorldUid, setHoveredPathUid, setActiveSelectedUid, setSelectedWorldUid, setSelectedPathUid, setPlacedSymbols,
        setSelectedLabelUid, setHoveredLabelUid, addLabel, updateLabel,
        onPointPlaced, addVertex, addPathVertex, insertPathVertex, movePathVertex,
        finishDrawing, finishPathDrawing, onCommit,
        eraseSymbolsAt, spraySymbolsAt, sprayStampsAt, snapRiverNode,
        updatePan, dirtyBgScreen, dirtyMidScreen, dirtyFg, triggerCameraUpdate,
        brush, activeSymbols, toast, tLimitReached, tLimitDesc,
    ]);

    const onWheel = useCallback((e: React.WheelEvent) => {
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const zoom = zoomRef.current;
        const pan = panRef.current;
        const newZoom = Math.min(Math.max(zoom * delta, 1.0), 5);
        if (newZoom !== zoom) {
            const rect = fgCanvasRef.current?.getBoundingClientRect();
            if (!rect) return;
            const sx = e.clientX - rect.left;
            const sy = e.clientY - rect.top;
            const dx = (sx - pan.x) * (1 - delta);
            const dy = (sy - pan.y) * (1 - delta);
            updateZoom(newZoom);
            updatePan({ x: pan.x + dx, y: pan.y + dy });
            dirtyBgScreen.current = true; dirtyMidScreen.current = true; dirtyFg.current = true;
            triggerCameraUpdate();
        }
    }, [zoomRef, panRef, fgCanvasRef, updateZoom, updatePan, dirtyBgScreen, dirtyMidScreen, dirtyFg, triggerCameraUpdate]);

    return { onEvent, onWheel };
}
